import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaTrash, FaEdit } from "react-icons/fa";
import Sidebar from '../component/Sidebar';
import toast from "react-hot-toast";
import Pagination from "../Master/Pagination";
import CollapsibleHeader from "../component/CollapsibleHeader";
import { API_BASE_URL } from "../config/apiConfig";

const TrainingList = () => {
  const [trainings, setTrainings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [employees, setEmployees] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [trainingRes, employeeRes] = await Promise.all([
        axios.get("/api/trainings"),
        axios.get("/api/employees")
      ]);
      setTrainings(trainingRes.data);
      setEmployees(employeeRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Error loading data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const indexOfLast = perPage === "all" ? trainings.length : currentPage * perPage;
  const indexOfFirst = perPage === "all" ? 0 : indexOfLast - perPage;
  const currentTrainings = perPage === "all" ? trainings : trainings.slice(indexOfFirst, indexOfLast);

  const deleteTraining = async (id) => {
    if (!window.confirm("Are you sure you want to delete this training?")) return;
    try {
      await axios.delete(`/api/trainings/${id}`);
      setTrainings(trainings.filter((t) => t._id !== id));
      toast.success("Training deleted successfully");
    } catch (err) {
      toast.error("Failed to delete training");
    }
  };

  const openPdf = (base64, fileName) => {
    const byteCharacters = atob(base64);
    const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const newTab = window.open(blobUrl, "_blank");

    if (newTab) {
      newTab.document.title = fileName;
    }
  };

const renderListFormat = (items) => {
    if (!items || items.length === 0) return "N/A";
    return (
      <ol className="list-decimal list-inside text-left">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>
    );
  };

  const renderEmployeeList = (selectedEmps) => {
    if (!selectedEmps || selectedEmps.length === 0) return "N/A";
    
    const isAll = selectedEmps.some(emp => emp.toUpperCase() === "ALL");
    let namesList = [];

    if (isAll) {
      namesList = employees.map(emp => `${emp.firstName} ${emp.lastName}`.trim());
    } else {
      namesList = selectedEmps.map(userId => {
        const foundEmp = employees.find(e => e.employeeUserId === userId || e.employeeID === userId);
        return foundEmp ? `${foundEmp.firstName} ${foundEmp.lastName}`.trim() : userId;
      });
    }

    return (
      <ol className="list-decimal list-inside text-left">
        {namesList.map((name, idx) => (
          <li key={idx}>{name}</li>
        ))}
      </ol>
    );
  };

  return (
    <div className="flex h-screen flex-col md:flex-row overflow-hidden">
      <Sidebar />
      <div className="page-panel px-2 pb-1 bg-white shadow-md rounded-md flex-1 flex flex-col overflow-hidden">
        <div className="page-sticky-header bg-white shrink-0">
          <CollapsibleHeader title="Training List Header" contentClassName="mt-1">
            <div className="page-title-bar flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <h2 className="text-xl font-bold text-dorika-blue">Training Records</h2>
              <div className="flex gap-3 items-end">
                <button onClick={() => navigate("/Training")} className="h-8 bg-dorika-orange hover:bg-dorika-blue text-white px-4 rounded font-semibold text-sm">
                  Add New Training
                </button>
              </div>
            </div>
          </CollapsibleHeader>
        </div>

        <div className="flex-1 overflow-auto mt-2 scrollbar-thin hover:scrollbar-default">
          <table className="table-auto w-full border border-dorika-blue text-sm">
            <thead className="bg-dorika-blue text-white sticky top-0 z-10">
              <tr>
                <th className="border px-2 py-1 w-12">SL No</th>
                <th className="border px-2 w-28">Date</th>
                <th className="border px-2 w-24">Start Time</th> 
                <th className="border px-2 w-24">End Time</th>
                <th className="border px-2 w-36">Title</th>
                <th className="border px-2 w-28">Category</th>
                <th className="border px-2 w-40">Department</th>
                <th className="border px-2 w-52">Selected Emp</th>
                <th className="border px-2 w-28">Document</th>
                <th className="border px-2 w-20">Action</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {currentTrainings.length > 0 ? (
                currentTrainings.map((t, index) => (
                  <tr key={t._id} className="hover:bg-dorika-blueLight transition align-top">
                    <td className="border px-2 py-2">{index + 1 + (currentPage - 1) * perPage}</td>
                    <td className="border px-2 py-2">{t.trainingDate}</td>
                    <td className="border px-2 py-2">{t.startTime}</td>
                    <td className="border px-2 py-2">{t.endTime}</td>
                    <td className="border px-2 py-2">{t.trainingTitle}</td>
                    <td className="border px-2 py-2">{t.trainingCategory}</td>
                    <td className="border px-2 py-2">{renderListFormat(t.departments)}</td>
                    <td className="border px-2 py-2">{renderEmployeeList(t.selectedEmployees)}</td>
                    <td className="border px-2 py-2">
                      {t.documentPaths && t.documentPaths.length > 0 ? (
                        t.documentPaths.map((pdfData, idx) => (
                          <button
                            key={idx}
                            onClick={() => openPdf(pdfData, t.documentNames?.[idx])}
                            className="text-blue-600 underline font-bold text-xs block mb-1"
                          >
                            {t.documentNames?.[idx] || `VIEW PDF ${idx + 1}`}
                          </button>
                        ))
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="border px-2 py-2">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => navigate("/Training", { state: { training: t } })} className="text-dorika-blue p-1"><FaEdit /></button>
                        <button onClick={() => deleteTraining(t._id)} className="text-dorika-orange p-1"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="10" className="py-4 text-gray-500">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="shrink-0 mt-2">
          {perPage !== "all" && (
            <Pagination
              total={trainings.length}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingList;